// Import from Angular.
import { ModuleWithProviders } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AppointmentsComponent } from 'app/appointments/appointments.component';
import { AdminLoginComponent } from 'app/authentication/admin/admin.component';
// Import extra files.
import { AuthGuard } from 'app/authentication/guard/auth.guard';
import { MyAccountCanDeactivate } from 'app/authentication/guard/my-account-can-deactivate.guard';
import { LoginComponent } from 'app/authentication/login/login.component';
import { LogoutComponent } from 'app/authentication/logout/logout.component';
import { AvailabilitiesComponent } from 'app/availabilities/availabilities.component';
import { CalendarComponent } from 'app/calendar/calendar.component';
import { ClientsAddComponent } from 'app/clients/add/clients-add.component';
import { ClientsComponent } from 'app/clients/clients.component';
import { ClientsDetailsComponent } from 'app/clients/details/clients-details.component';
import { ClientsEditComponent } from 'app/clients/edit/clients-edit.component';
import { InsightsPageComponent } from 'modules/insights/components/insights-page/insights-page.component';
import { UnleashGuard } from 'shared/unleash/unleash.guard';

import { AddressComponent } from '../modules/address/pages/address/address.component';
import { AccountPremiumComponent } from './account/premium/premium.component';
import { ReasonsComponent } from './appointments/reasons/reasons.component';
import { CheckoutSuccessComponent } from './checkout/checkout-success/checkout-success.component';
import { CheckoutComponent } from './checkout/checkout/checkout.component';
import { FinancialsComponent } from './financials/financials.component';
import { HasAppointment } from './guards/hasappointment';
import { MyAccountComponent } from './my-account/my-account.component';
import { SignupComponent } from './signup/signup.component';

// Define routes.
export const AppRoutes: Routes = [
	{ path: '', component: CalendarComponent, canActivate: [AuthGuard] },
	{
		path: 'appointments',
		component: AppointmentsComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'appointments/reasons',
		component: ReasonsComponent,
		canActivate: [AuthGuard, HasAppointment],
	},
	{
		path: 'appointments/add/:id',
		component: AppointmentsComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'appointments/:id/:type',
		component: AppointmentsComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'appointments/:id',
		component: AppointmentsComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'availabilities',
		component: AvailabilitiesComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'account',
		children: [
			{
				path: '',
				pathMatch: 'full',
				component: MyAccountComponent,
				canActivate: [AuthGuard],
				data: {
					pageName: 'account',
				},
			},
			{
				path: 'personal',
				component: MyAccountComponent,
				canActivate: [AuthGuard],
				canDeactivate: [MyAccountCanDeactivate],
				data: {
					pageName: 'personal',
				},
			},
			{
				path: 'professional',
				component: MyAccountComponent,
				canActivate: [AuthGuard],
				canDeactivate: [MyAccountCanDeactivate],
				data: {
					pageName: 'professional',
				},
			},
			{
				path: 'definitions',
				component: MyAccountComponent,
				canActivate: [AuthGuard],
				canDeactivate: [MyAccountCanDeactivate],
				data: {
					pageName: 'definitions',
				},
			},
			{
				path: 'online-sessions',
				component: MyAccountComponent,
				canActivate: [AuthGuard],
				canDeactivate: [MyAccountCanDeactivate],
				data: {
					pageName: 'online-sessions',
				},
			},
			{
				path: 'onsite-sessions',
				component: MyAccountComponent,
				canActivate: [AuthGuard, UnleashGuard],
				canDeactivate: [MyAccountCanDeactivate],
				data: {
					pageName: 'onsite-sessions',
					toggleName: 'onsite-sessions',
					redirectUrl: '/account/online-sessions',
				},
			},
			{
				path: 'payment',
				component: MyAccountComponent,
				canActivate: [AuthGuard],
				data: {
					pageName: 'payment',
				},
			},
		],
	},
	{
		path: 'account/premium',
		component: AccountPremiumComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'account/address/new',
		component: AddressComponent,
		canActivate: [AuthGuard, UnleashGuard],
		data: {
			action: 'create',
			pageName: 'account/address/new',
			toggleName: 'onsite-sessions',
			redirectUrl: '/account/online-sessions',
		},
	},
	{
		path: 'account/address/edit/:id',
		component: AddressComponent,
		canActivate: [AuthGuard, UnleashGuard],
		data: {
			action: 'edit',
			pageName: 'account/address/edit',
			toggleName: 'onsite-sessions',
			redirectUrl: '/account/online-sessions',
		},
	},
	{ path: 'calendar', component: CalendarComponent, canActivate: [AuthGuard] },
	{
		path: 'calendar/:date',
		component: CalendarComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'clients',
		component: ClientsComponent,
		canActivate: [AuthGuard],
		children: [
			{ path: 'add', component: ClientsAddComponent, canActivate: [AuthGuard] },
			{
				path: 'details/:id',
				component: ClientsDetailsComponent,
				canActivate: [AuthGuard],
			},
			{
				path: 'edit/:id',
				component: ClientsEditComponent,
				canActivate: [AuthGuard],
			},
			{ path: 'list', component: ClientsComponent, canActivate: [AuthGuard] },
			{ path: '', component: ClientsComponent, canActivate: [AuthGuard] },
		],
	},
	{
		path: 'financials',
		component: FinancialsComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'checkout',
		canActivate: [AuthGuard],
		children: [
			{ path: '', component: CheckoutComponent, canActivate: [AuthGuard] },
			{
				path: 'success',
				component: CheckoutSuccessComponent,
				canActivate: [AuthGuard],
			},
		],
	},
	{
		path: 'insights',
		component: InsightsPageComponent,
		canActivate: [AuthGuard],
	},
	{ path: 'signup', component: SignupComponent },
	{ path: 'login', component: LoginComponent },
	{ path: 'adminlogin', component: AdminLoginComponent },
	{ path: 'logout', component: LogoutComponent },
	{ path: 'search', component: CalendarComponent, canActivate: [AuthGuard] },
	{ path: 'schedule', redirectTo: 'calendar' },
	{ path: 'home', redirectTo: 'calendar' },
	{ path: 'logoff', redirectTo: 'logout' },
	{ path: '**', redirectTo: 'calendar' },
];

// Export router.
export const routing: ModuleWithProviders<any> = RouterModule.forRoot(
	AppRoutes,
	{ relativeLinkResolution: 'legacy' }
);
